import { useReactiveVar } from '@apollo/client'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { isContentTeam } from 'api/graphQL/reactiveVars/workspaceContent'
import { showBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import { useSelectedProjectElementNode } from 'components/WorkspaceEditor/Workspace2Store'
import { useMemo } from 'react'
import { useIsBestPracticeTagDisabled } from 'shared/utils/bestPracticeUtils'
import { BestPracticeContentItem } from './useBestPracticesData'

/**
 * Determines whether Best Practices icons should be displayed for a given content item.
 *
 * @param contentItem - The content item to check (can be DataNode or ContentItemViewNodesType)
 * @returns Object with display flags
 */
export const useBestPracticesDisplay = (
    contentItem: BestPracticeContentItem
) => {
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const showBPSetting = useReactiveVar(showBestPracticeSetting)
    const selectedProjectElementNode = useSelectedProjectElementNode()
    const isAddedToProject = selectedProjectElementNode.isInProject
    const isImported =
        contentItem.isImported ?? contentItem.contentItemView?.isImported
    const isDeleted =
        contentItem.isDeleted ?? contentItem.contentItemView?.isDeleted

    // Check if BP editing is allowed based on permissions
    const canEditBP = !useIsBestPracticeTagDisabled({
        userInfo,
        isInProject: isAddedToProject,
        contentTeamFromStudioPage: isContentTeamUser,
        isProjectImported: isImported,
        isViewOnly: false,
    })

    // Check if BP viewing is allowed (more permissive than editing)
    const canViewBP = !useIsBestPracticeTagDisabled({
        userInfo,
        isInProject: isAddedToProject,
        contentTeamFromStudioPage: isContentTeamUser,
        isProjectImported: isImported,
        isViewOnly: true,
    })
    const shouldDisplayBPIcon = useMemo(() => {
        // Don't show if BP feature is disabled globally or content is deleted
        if (!showBPSetting || isDeleted) {
            return false
        }

        // Show if viewing is allowed (icons should be visible even if editing is disabled)
        return canViewBP
    }, [showBPSetting, canViewBP, isDeleted])

    const shouldEnableBPEdit = useMemo(() => {
        return shouldDisplayBPIcon && canEditBP
    }, [shouldDisplayBPIcon, canEditBP])

    return {
        shouldDisplayBPIcon,
        shouldEnableBPEdit,
        canEditBP,
        canViewBP,
    }
}
